import { z } from 'zod';

// src/models/generated/commons_evidence_models.ts
var EvidenceSourceSchema = z.object({ "kind": z.enum(["trace_opcode", "registry", "call_shape", "abi", "proxy_abi", "wire_layout", "provided", "generic", "heuristic", "effects", "provider", "user", "automation", "unknown", "aggregate"]).describe("How / by whom an :class:`Evidence` assertion was produced.\n\nThe decode ``method`` values (``abi`` \u2026 ``generic``) and the catalog origin\nvalues (``provider`` / ``user`` / ``automation``) live in one enum so the\nscoring engine can price any producer from a single ``method_base`` table.").default("abi"), "id": z.union([z.string(), z.null()]).default(null), "external_id": z.union([z.string(), z.null()]).default(null), "url": z.union([z.string(), z.null()]).default(null), "raw_payload_ref": z.union([z.string(), z.null()]).default(null), "visibility": z.union([z.enum(["internal", "external"]).describe('Whether an evidence source\'s IDENTITY may be shown to external customers.\n\nConfidence, lifecycle and the fact that a row is corroborated are always\nfair game \u2014 that is the "defensible answer". This governs only the *who*:\n\n- ``INTERNAL`` \u2014 a third-party source we scrape / ingest. External audiences\n  see it collapsed to a neutral first-party rollup\n  (:data:`EXTERNAL_SOURCE_LABEL`), so a customer never learns we source from\n  that provider.\n- ``EXTERNAL`` \u2014 a human operator / first-party / registry / decode\n  assertion whose *kind* is safe to surface (its raw ids/urls are still\n  dropped on redaction).'), z.null()]).default(null) }).describe('Who / how asserted an :class:`Evidence` entry.\n\n``id`` is the source-local identifier: a provider slug (``"moralis"``), an\nABI source (``"sourcify"``), a protocol-plugin id, or a user id \u2014 depending\non :attr:`kind`. For ``kind == PROVIDER`` the id is normalised to a\ncanonical provider slug via :func:`commons.providers.normalize_provider_id`\n(mirrors the old ``ProviderAttribution.provider_id`` discipline).');
var EvidenceSchema = z.object({ "source": z.object({ "kind": z.enum(["trace_opcode", "registry", "call_shape", "abi", "proxy_abi", "wire_layout", "provided", "generic", "heuristic", "effects", "provider", "user", "automation", "unknown", "aggregate"]).describe("How / by whom an :class:`Evidence` assertion was produced.\n\nThe decode ``method`` values (``abi`` \u2026 ``generic``) and the catalog origin\nvalues (``provider`` / ``user`` / ``automation``) live in one enum so the\nscoring engine can price any producer from a single ``method_base`` table.").default("abi"), "id": z.union([z.string(), z.null()]).default(null), "external_id": z.union([z.string(), z.null()]).default(null), "url": z.union([z.string(), z.null()]).default(null), "raw_payload_ref": z.union([z.string(), z.null()]).default(null), "visibility": z.union([z.enum(["internal", "external"]).describe('Whether an evidence source\'s IDENTITY may be shown to external customers.\n\nConfidence, lifecycle and the fact that a row is corroborated are always\nfair game \u2014 that is the "defensible answer". This governs only the *who*:\n\n- ``INTERNAL`` \u2014 a third-party source we scrape / ingest. External audiences\n  see it collapsed to a neutral first-party rollup\n  (:data:`EXTERNAL_SOURCE_LABEL`), so a customer never learns we source from\n  that provider.\n- ``EXTERNAL`` \u2014 a human operator / first-party / registry / decode\n  assertion whose *kind* is safe to surface (its raw ids/urls are still\n  dropped on redaction).'), z.null()]).default(null) }).describe('Who / how asserted an :class:`Evidence` entry.\n\n``id`` is the source-local identifier: a provider slug (``"moralis"``), an\nABI source (``"sourcify"``), a protocol-plugin id, or a user id \u2014 depending\non :attr:`kind`. For ``kind == PROVIDER`` the id is normalised to a\ncanonical provider slug via :func:`commons.providers.normalize_provider_id`\n(mirrors the old ``ProviderAttribution.provider_id`` discipline).').optional(), "confidence": z.number().gte(0).lte(1).default(1), "reasons": z.array(z.string()).optional(), "signals": z.record(z.string(), z.any()).optional(), "asserted_fields": z.array(z.string()).optional(), "asserted_at": z.union([z.number().int(), z.null()]).default(null), "last_seen_at": z.union([z.number().int(), z.null()]).default(null), "context": z.record(z.string(), z.any()).optional(), "metadata": z.record(z.string(), z.any()).optional() }).describe("One assertion: *this source claims X, with this confidence, because \u2026*.\n\nReplaces both ``SemanticEvidence`` (decode) and ``ProviderAttribution``\n(catalog). ``confidence`` is clamped to ``[0, 1]``. ``asserted_at`` /\n``last_seen_at`` default to wall-clock seconds so a fresh assertion merges\nwith an older one without a ``None`` branch (see\n:func:`commons.evidence.engine.merge_evidence`).\n\nDecode-specific coordinates (chain / block / cross-chain hop / proxy\ntraversal) live in :attr:`context` under the ``CTX_*`` keys; read them via\nthe convenience properties (:attr:`chain_id`, :attr:`dest_chain_id`, \u2026).");
var MEDIA_REF_KINDS = ["placeholder", "cdn_key", "url"];
var GRAPH_KINDS = ["semantic", "flow", "interaction", "knowledge"];
var GRAPH_LAYOUT_HINTS = ["hierarchical", "temporal", "force"];
var SCENE_NODE_KINDS = [
  "address",
  "contract",
  "entity",
  "cluster",
  "exchange",
  "defi",
  "token",
  "oracle",
  "bridge",
  "don",
  "mixer",
  "virtual",
  "verification",
  "attestation",
  "relayer",
  "message",
  "settlement"
];
var OFFCHAIN_SCENE_NODE_KINDS = [
  "entity",
  "cluster",
  "exchange",
  "oracle",
  "bridge",
  "don",
  "mixer",
  "virtual",
  "verification",
  "attestation",
  "relayer",
  "settlement"
];
var SCENE_EDGE_KINDS = [
  "approval",
  "transfer",
  "call",
  "contains",
  "bridge_out",
  "bridge_in",
  "cross_chain",
  "flow",
  "verify",
  "creates"
];
var SCENE_GROUP_TYPES = ["chain", "protocol", "cluster"];
var SCENE_DETAIL_TIERS = ["compact", "standard", "expanded"];
var SCENE_EMPTY_REASONS = [
  "no_semantic_tree",
  "decluttered_empty",
  "no_flow_source",
  "no_value_moved",
  "new_issuance",
  "shielded",
  "pooled"
];
var SCENE_EMPTY_REASON_ATTR = "empty_reason";
var SCENE_ELEMENT_ORIGINS = ["decoded", "synthesized", "user"];
var MediaRefSchema = z.object({
  kind: z.string().default("placeholder"),
  value: z.string(),
  fallback: z.union([z.string(), z.null()]).default(null),
  alt: z.union([z.string(), z.null()]).default(null)
});
var SceneAssetSchema = z.object({
  token_id: z.union([z.string(), z.null()]).default(null),
  token_address: z.union([z.string(), z.null()]).default(null),
  token_chain_id: z.union([z.string(), z.null()]).default(null),
  symbol: z.union([z.string(), z.null()]).default(null),
  decimals: z.union([z.number().int(), z.null()]).default(null),
  amount_raw: z.union([z.string(), z.null()]).default(null),
  amount: z.union([z.string(), z.null()]).default(null),
  usd: z.union([z.string(), z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  // 'actual' | 'quoted_minimum' — mirrors commons AmountBasis. Lets a label
  // flag a swap min-return floor as "(min)" instead of an exact amount.
  basis: z.union([z.string(), z.null()]).default(null),
  min_amount_raw: z.union([z.string(), z.null()]).default(null),
  min_amount: z.union([z.string(), z.null()]).default(null),
  max_amount_raw: z.union([z.string(), z.null()]).optional(),
  max_amount: z.union([z.string(), z.null()]).optional(),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  // The well-known asset this token CLAIMS to be and is not (see
  // `commons.tokens.impersonation`). Its `symbol` stays what it calls itself;
  // a renderer must not show that bare. Absent for an honest token.
  impersonates: z.union([z.record(z.string(), z.unknown()), z.null()]).optional()
});
var SceneSourceRefSchema = z.object({
  ref_type: z.string(),
  address: z.union([z.string(), z.null()]).default(null),
  log_index: z.union([z.number().int(), z.null()]).default(null),
  internal_tx_index: z.union([z.number().int(), z.null()]).default(null),
  path: z.array(z.string()).default([])
});
var SceneFactSchema = z.object({
  key: z.string(),
  label: z.string().default(""),
  kind: z.union([z.string(), z.null()]).default(null),
  value: z.unknown(),
  protocol_id: z.union([z.string(), z.null()]).default(null),
  source: z.union([z.string(), z.null()]).default(null),
  path_id: z.union([z.string(), z.null()]).default(null),
  semantic_node_id: z.union([z.string(), z.null()]).default(null),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  evidence: z.array(EvidenceSchema).default([]),
  confidence: z.union([z.number().min(0).max(1), z.null()]).default(null),
  icon: z.union([z.string(), z.null()]).default(null),
  description: z.union([z.string(), z.null()]).default(null)
});
var SceneNodeSchema = z.object({
  id: z.string(),
  kind: z.string().default("address"),
  label: z.string().default(""),
  sublabel: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  address: z.union([z.string(), z.null()]).default(null),
  group_id: z.union([z.string(), z.null()]).default(null),
  off_chain: z.boolean().default(false),
  entity_id: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  assets: z.array(SceneAssetSchema).default([]),
  value: z.union([z.string(), z.null()]).default(null),
  badge: z.union([z.string(), z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  evidence: z.array(EvidenceSchema).default([]),
  confidence: z.number().min(0).max(1).default(1),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  kind: z.string().default("flow"),
  label: z.union([z.string(), z.null()]).default(null),
  asset: z.union([SceneAssetSchema, z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  from_chain: z.union([z.string(), z.null()]).default(null),
  to_chain: z.union([z.string(), z.null()]).default(null),
  cross_chain: z.boolean().default(false),
  virtual: z.boolean().default(false),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var MERGED_COUNT_ATTR = "merged_count";
var MERGED_TXS_ATTR = "merged_txs";
var MERGED_TXS_LIMIT = 100;
function mergedTxRef(parts) {
  const id = parts.canonicalTxId || parts.txHash;
  if (!id) return void 0;
  return {
    id,
    ...parts.txHash ? { tx_hash: parts.txHash } : {},
    ...parts.chainId ? { chain_id: parts.chainId } : {}
  };
}
function mergedTxRefs(attributes) {
  return parseTxRefs(attributes?.[MERGED_TXS_ATTR]);
}
var TX_MEMBERSHIP_ATTR = "in_txs";
function txMembership(attributes) {
  return parseTxRefs(attributes?.[TX_MEMBERSHIP_ATTR]);
}
function groupSceneLegs(attributes) {
  const raw = attributes?.legs;
  if (!Array.isArray(raw)) return [];
  const legs = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const { id, role, chain_id, tx_hash } = entry;
    if (typeof id !== "string" || !id) continue;
    legs.push({
      id,
      index: legs.length,
      ...typeof role === "string" && role ? { role } : {},
      ...typeof chain_id === "string" && chain_id ? { chain_id } : {},
      ...typeof tx_hash === "string" && tx_hash ? { tx_hash } : {}
    });
  }
  return legs;
}
function parseTxRefs(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = /* @__PURE__ */ new Set();
  const refs = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const { id, tx_hash, chain_id, amount_raw } = entry;
    if (typeof id !== "string" || !id || seen.has(id)) continue;
    seen.add(id);
    refs.push({
      id,
      ...typeof tx_hash === "string" && tx_hash ? { tx_hash } : {},
      ...typeof chain_id === "string" && chain_id ? { chain_id } : {},
      ...typeof amount_raw === "string" && /^-?\d+$/.test(amount_raw) ? { amount_raw } : {}
    });
  }
  return refs;
}
var TX_COUNT_ATTR = "tx_count";
function txCountSublabel(count) {
  return `${count} tx`;
}
function isTxCountSublabel(sublabel) {
  return sublabel == null || sublabel === "" || /^\d+ tx$/.test(sublabel);
}
function mergedCount(attributes) {
  const value = attributes?.[MERGED_COUNT_ATTR];
  return typeof value === "number" && value > 0 ? value : 1;
}
var SceneGroupSchema = z.object({
  id: z.string(),
  group_type: z.string().default("chain"),
  chain_id: z.union([z.string(), z.null()]).default(null),
  label: z.string().default(""),
  member_node_ids: z.array(z.string()).default([]),
  color: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
var GraphSceneSchema = z.object({
  version: z.string().default("1"),
  kind: z.string().default("semantic"),
  summary: z.union([z.string(), z.null()]).default(null),
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]).transform((edges) => {
    const reserved = new Set(edges.map((edge) => edge.id));
    const seen = /* @__PURE__ */ new Set();
    return edges.map((edge) => {
      if (!seen.has(edge.id)) {
        seen.add(edge.id);
        return edge;
      }
      let suffix = 2;
      let id = `${edge.id}:duplicate:${suffix}`;
      while (reserved.has(id)) id = `${edge.id}:duplicate:${++suffix}`;
      reserved.add(id);
      return { ...edge, id };
    });
  }),
  groups: z.array(SceneGroupSchema).default([]),
  // The transaction's headline "business metrics" — the SAME resolved
  // `key_attributes` groups the detail-page highlights panel shows, carried on
  // EVERY chart kind so the graph view renders a consistent highlights strip.
  // Kept as the raw snake_case wire shape (a passthrough) so the wire layer
  // stays below `storage`; a consumer normalises it with `parseKeyAttributeGroups`
  // (the same parser the detail panel + group view use) → `KeyAttributeGroup[]`.
  highlights: z.array(z.record(z.string(), z.unknown())).default([]),
  chain_ids: z.array(z.string()).default([]),
  multi_chain: z.boolean().default(false),
  layout_hint: z.string().default("hierarchical"),
  // Compact-first: mirrors the backend `GraphBuildPolicy.default_view_tier`
  // default so a scene missing the field opens on the minimal intent + highlights
  // (the user raises the tier live). The scene always carries every tier.
  default_detail_tier: z.string().default("compact"),
  attributes: z.record(z.string(), z.unknown()).default({})
});
function parseGraphScene(value) {
  return GraphSceneSchema.parse(value);
}
var SceneOverlaySchema = z.object({
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  hidden_node_ids: z.array(z.string()).default([]),
  hidden_edge_ids: z.array(z.string()).default([]),
  node_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  edge_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  /**
   * Where the author put an authored node. Honoured as a pin, not a hint, and
   * only for nodes the overlay itself added — pinning a decoded node freezes a
   * layout the backend is entitled to change, and the position was never the
   * author's to give.
   */
  positions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })).default({}),
  /**
   * Who asserted this overlay, when, and why. Stamped by the service from the
   * caller's identity, never read from the element bodies — a client can no more
   * forge an author than it can forge `origin`.
   */
  authored_by: z.union([z.string(), z.null()]).default(null),
  authored_at: z.union([z.string(), z.null()]).default(null),
  authored_reason: z.union([z.string(), z.null()]).default(null),
  /**
   * The tier this overlay was published at, stamped by the read.
   *
   * On the BODY rather than left on the row, because the body is all a fold
   * receives — and without it a fold cannot tell one overlay overriding a lower
   * rung (precedence working) from two peers at the same rung contradicting each
   * other. Written by `overlay_wire` on the API and by the tx_graph store.
   */
  authored_scope: z.union([z.string(), z.null()]).default(null)
});
var AUTHORED_BY_ATTR = "authored_by";
var AUTHORED_AT_ATTR = "authored_at";
var AUTHORED_REASON_ATTR = "authored_reason";
var AUTHORED_SCOPE_ATTR = "authored_scope";
var CONTESTED_ATTR = "contested";
function emptySceneOverlay() {
  return {
    nodes: [],
    edges: [],
    groups: [],
    hidden_node_ids: [],
    hidden_edge_ids: [],
    node_patches: {},
    edge_patches: {},
    positions: {},
    authored_by: null,
    authored_at: null,
    authored_reason: null,
    authored_scope: null
  };
}
var SUBJECT_ANY = "*";
var OVERLAY_BODY_KEYS = /* @__PURE__ */ new Set([
  "nodes",
  "edges",
  "groups",
  "hidden_node_ids",
  "hidden_edge_ids",
  "node_patches",
  "edge_patches",
  "positions",
  AUTHORED_BY_ATTR,
  AUTHORED_AT_ATTR,
  AUTHORED_REASON_ATTR,
  AUTHORED_SCOPE_ATTR
]);
function isSceneOverlayBody(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  return Object.keys(value).some((key) => OVERLAY_BODY_KEYS.has(key));
}
function annotationLayers(annotations, subjectKey) {
  const read = (value) => {
    try {
      return parseSceneOverlay(value);
    } catch {
      return emptySceneOverlay();
    }
  };
  if (isSceneOverlayBody(annotations)) return { [subjectKey]: read(annotations) };
  if (typeof annotations !== "object" || annotations === null) return {};
  const out = {};
  for (const [key, value] of Object.entries(annotations)) {
    if (typeof value === "object" && value !== null) out[key] = read(value);
  }
  return out;
}
function mergeAnnotationLayer(annotations, subjectKey, overlay) {
  return { ...annotationLayers(annotations, subjectKey), [subjectKey]: overlay };
}
function layerForSubject(annotations, subjectKey) {
  return annotationLayers(annotations, subjectKey)[subjectKey] ?? emptySceneOverlay();
}
function parseSceneOverlay(value) {
  return SceneOverlaySchema.parse(value);
}
var GROUPING_DIMENSIONS = [
  "related",
  "hop",
  "chain",
  "protocol",
  "entity",
  "tag",
  "label",
  "catalog_group",
  "kind",
  "saga",
  "transaction"
];
var GroupingDimensionOptionsSchema = z.object({
  /** Drop a band with fewer members than this (a band of one is just a node). */
  min_members: z.number().int().positive().optional(),
  /** Keep at most this many bands (largest first); the rest go ungrouped. */
  max_bands: z.number().int().positive().optional()
});
var GroupingSpecSchema = z.object({
  dimensions: z.array(z.enum(GROUPING_DIMENSIONS)).default([]),
  options: z.record(z.string(), GroupingDimensionOptionsSchema).default({})
});
function defaultGroupingSpec() {
  return { dimensions: ["chain"] };
}
function parseGroupingSpec(value) {
  if (!value || typeof value !== "object") return null;
  const raw = value;
  if (!Array.isArray(raw.dimensions)) return null;
  const known = new Set(GROUPING_DIMENSIONS);
  const seen = /* @__PURE__ */ new Set();
  const dimensions = [];
  for (const entry of raw.dimensions) {
    if (typeof entry !== "string" || !known.has(entry) || seen.has(entry)) continue;
    seen.add(entry);
    dimensions.push(entry);
  }
  const options = {};
  if (raw.options && typeof raw.options === "object") {
    for (const [key, opts] of Object.entries(raw.options)) {
      if (!known.has(key)) continue;
      const parsed = GroupingDimensionOptionsSchema.safeParse(opts);
      if (parsed.success) options[key] = parsed.data;
    }
  }
  return Object.keys(options).length > 0 ? { dimensions, options } : { dimensions };
}

export { AUTHORED_AT_ATTR, AUTHORED_BY_ATTR, AUTHORED_REASON_ATTR, AUTHORED_SCOPE_ATTR, CONTESTED_ATTR, EvidenceSchema, EvidenceSourceSchema, GRAPH_KINDS, GRAPH_LAYOUT_HINTS, GROUPING_DIMENSIONS, GraphSceneSchema, GroupingDimensionOptionsSchema, GroupingSpecSchema, MEDIA_REF_KINDS, MERGED_COUNT_ATTR, MERGED_TXS_ATTR, MERGED_TXS_LIMIT, MediaRefSchema, OFFCHAIN_SCENE_NODE_KINDS, OVERLAY_BODY_KEYS, SCENE_DETAIL_TIERS, SCENE_EDGE_KINDS, SCENE_ELEMENT_ORIGINS, SCENE_EMPTY_REASONS, SCENE_EMPTY_REASON_ATTR, SCENE_GROUP_TYPES, SCENE_NODE_KINDS, SUBJECT_ANY, SceneAssetSchema, SceneEdgeSchema, SceneFactSchema, SceneGroupSchema, SceneNodeSchema, SceneOverlaySchema, SceneSourceRefSchema, TX_COUNT_ATTR, TX_MEMBERSHIP_ATTR, annotationLayers, defaultGroupingSpec, emptySceneOverlay, groupSceneLegs, isSceneOverlayBody, isTxCountSublabel, layerForSubject, mergeAnnotationLayer, mergedCount, mergedTxRef, mergedTxRefs, parseGraphScene, parseGroupingSpec, parseSceneOverlay, txCountSublabel, txMembership };
//# sourceMappingURL=chunk-J77YJ7IT.js.map
//# sourceMappingURL=chunk-J77YJ7IT.js.map